// Client-side helpers for event registration forms. Mirrors the rules and
// messages in backend/src/utils/registrationForm.js so client and server agree.

export const FIELD_KINDS = [
  { value: 'text', label: 'Short text' },
  { value: 'longtext', label: 'Paragraph' },
  { value: 'choice', label: 'Single choice' },
  { value: 'multichoice', label: 'Multiple choice' },
  { value: 'date', label: 'Date' },
  { value: 'stars', label: 'Star rating (1-5)' },
]

// Events whose hardcoded forms are served by a legacy formType and must not
// get a custom registrationForm (same values as the backend).
export const LEGACY_LOCKED_FORM_TYPES = new Set([
  'extended-workshop',
  'extended-form2',
  'unmasking-halfday-apr2026',
  'a-way-to-move',
])

export const OTHER_PREFIX = 'Other: '

const MAX_FIELDS = 30
const MAX_LABEL = 200
const MAX_HELP_TEXT = 500
const MIN_CHOICES = 2
const MAX_CHOICES = 20
const MAX_CHOICE = 100
const MAX_ANSWER = 2000
const ID_RE = /^[a-z0-9][a-z0-9-]{0,63}$/
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/
const KIND_VALUES = FIELD_KINDS.map((k) => k.value)
const OTHER_TOKEN = OTHER_PREFIX.trim()

// Curly apostrophes/quotes are dropped like their straight counterparts.
const QUOTES_RE = new RegExp(`['"${String.fromCharCode(8216, 8217, 8220, 8221)}]`, 'g')

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v)

export const isChoiceKind = (kind) => kind === 'choice' || kind === 'multichoice'

export const generateFieldId = (label, existingIds = []) => {
  const base = String(label ?? '')
    .toLowerCase()
    .replace(QUOTES_RE, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/, '') || 'field'
  const taken = new Set(existingIds)
  if (!taken.has(base)) return base
  for (let n = 2; ; n++) {
    const suffix = `-${n}`
    const candidate = `${base.slice(0, 64 - suffix.length).replace(/-+$/, '')}${suffix}`
    if (!taken.has(candidate)) return candidate
  }
}

// ids are assigned by the builder from the label while draft is true (the
// label is still being typed); draft is transient and stripped on submit.
// existingIds is accepted for signature parity.
// eslint-disable-next-line no-unused-vars
export const newField = (existingIds) => ({
  draft: true,
  id: '',
  kind: 'text',
  label: '',
  required: false,
  helpText: '',
  choices: [],
  allowOther: false,
})

const fieldName = (rawLabel, index) => {
  const label = typeof rawLabel === 'string' ? rawLabel.trim() : ''
  return label && label.length <= MAX_LABEL ? `"${label}"` : `Question ${index + 1}`
}

const validateChoices = (raw, name) => {
  if (!Array.isArray(raw)) return `${name} needs a list of choices`
  const choices = raw.map((c) => (typeof c === 'string' ? c.trim() : ''))
  if (choices.length < MIN_CHOICES || choices.length > MAX_CHOICES) {
    return `${name} needs between ${MIN_CHOICES} and ${MAX_CHOICES} choices`
  }
  const seen = new Set()
  for (const c of choices) {
    if (!c || c.length > MAX_CHOICE) return `${name}: each choice must be 1-${MAX_CHOICE} characters`
    const key = c.toLowerCase()
    if (seen.has(key)) return `${name} has a duplicate choice "${c}"`
    seen.add(key)
  }
  return null
}

const validateFieldClient = (raw, i, ids) => {
  if (!isPlainObject(raw)) return `Question ${i + 1} is invalid`
  const name = fieldName(raw.label, i)

  const label = typeof raw.label === 'string' ? raw.label.trim() : ''
  if (!label) return `Question ${i + 1} needs a label`
  if (label.length > MAX_LABEL) return `Question ${i + 1}: label must be at most ${MAX_LABEL} characters`

  if (!KIND_VALUES.includes(raw.kind)) return `${name} has an unknown kind "${String(raw.kind)}"`

  const id = typeof raw.id === 'string' ? raw.id.trim() : ''
  if (!ID_RE.test(id)) return `${name} has an invalid id (use lowercase letters, digits and dashes)`
  if (ids.has(id)) return `${name} has a duplicate id "${id}"`
  ids.add(id)

  if (raw.helpText != null && typeof raw.helpText !== 'string') return `${name}: help text must be text`
  if ((raw.helpText ?? '').trim().length > MAX_HELP_TEXT) {
    return `${name}: help text must be at most ${MAX_HELP_TEXT} characters`
  }

  if (isChoiceKind(raw.kind)) return validateChoices(raw.choices, name)
  return null
}

// Returns the first error message, or null when the definition is valid.
// null/undefined means "no inline form", which is valid.
export const validateDefinitionClient = (def) => {
  if (def === null || def === undefined) return null
  if (!isPlainObject(def) || !Array.isArray(def.fields)) return 'Registration form must have a list of fields'
  if (def.fields.length > MAX_FIELDS) return `A registration form can have at most ${MAX_FIELDS} questions`
  const ids = new Set()
  for (let i = 0; i < def.fields.length; i++) {
    const err = validateFieldClient(def.fields[i], i, ids)
    if (err) return err
  }
  return null
}

// Shape sent to the API: no empty helpText, no choice data on non-choice kinds.
// Returns null when there are no fields (clears the form).
export const toSubmitDefinition = (def) => {
  if (!def || !Array.isArray(def.fields) || def.fields.length === 0) return null
  const fields = def.fields.map((f) => {
    const out = {
      id: typeof f.id === 'string' ? f.id.trim() : f.id,
      kind: f.kind,
      label: typeof f.label === 'string' ? f.label.trim() : f.label,
      required: f.required === true,
    }
    const helpText = typeof f.helpText === 'string' ? f.helpText.trim() : ''
    if (helpText) out.helpText = helpText
    if (isChoiceKind(f.kind)) {
      out.choices = (f.choices ?? []).map((c) => (typeof c === 'string' ? c.trim() : c))
      out.allowOther = f.allowOther === true
    }
    return out
  })
  return { version: 1, fields }
}

// A stars value of 0 means "not rated" (the UI's cleared state), like empty.
const isUnanswered = (v) =>
  v === null || v === undefined || v === '' || v === 0 || (Array.isArray(v) && v.length === 0)

// Result shape for the clean* helpers: { error } or { value } (value null = unanswered).
const cleanString = (v, name) => {
  if (typeof v !== 'string') return { error: `${name} must be text` }
  const s = v.trim()
  if (s.length > MAX_ANSWER) return { error: `${name} must be at most ${MAX_ANSWER} characters` }
  return { value: s || null }
}

// A single choice entry: a listed choice, or "Other: <text>" when allowed.
// value is null for a blank "Other:".
const cleanChoice = (v, field, name) => {
  const s = typeof v === 'string' ? v.trim() : ''
  if (field.choices.includes(s)) return { value: s }
  if (s.startsWith(OTHER_TOKEN) && field.allowOther) {
    const text = s.slice(OTHER_TOKEN.length).trim()
    if (!text) return { value: null }
    if (text.length > MAX_ANSWER) return { error: `${name} must be at most ${MAX_ANSWER} characters` }
    return { value: `${OTHER_PREFIX}${text}` }
  }
  return { error: `${name} has an invalid choice` }
}

const cleanAnswer = (field, v) => {
  const name = `"${field.label}"`
  const f = { ...field, choices: field.choices ?? [] }
  switch (field.kind) {
    case 'text':
    case 'longtext':
      return cleanString(v, name)
    case 'choice':
      if (typeof v !== 'string') return { error: `${name} must be a single choice` }
      return cleanChoice(v, f, name)
    case 'multichoice': {
      if (!Array.isArray(v)) return { error: `${name} must be a list of choices` }
      const picked = []
      for (const entry of v) {
        const r = cleanChoice(entry, f, name)
        if (r.error) return r
        if (r.value !== null && !picked.includes(r.value)) picked.push(r.value)
      }
      if (picked.filter((c) => !f.choices.includes(c)).length > 1) {
        return { error: `${name} can have only one "Other" answer` }
      }
      return { value: picked.length ? picked : null }
    }
    case 'date': {
      const r = cleanString(v, name)
      if (r.error || r.value === null) return r
      const s = r.value
      const d = DATE_RE.test(s) ? new Date(`${s}T00:00:00Z`) : null
      if (!d || Number.isNaN(d.getTime()) || !d.toISOString().startsWith(s)) {
        return { error: `${name} must be a valid date (YYYY-MM-DD)` }
      }
      return { value: s }
    }
    case 'stars':
      if (typeof v !== 'number' || !Number.isInteger(v) || v < 1 || v > 5) {
        return { error: `${name} must be a whole number from 1 to 5` }
      }
      return { value: v }
    default:
      return { error: `${name} has an unknown kind` }
  }
}

// Returns the first error message, or null when the answers satisfy the definition.
export const validateAnswersClient = (def, answers) => {
  const input = isPlainObject(answers) ? answers : {}
  for (const field of def?.fields ?? []) {
    const raw = Object.prototype.hasOwnProperty.call(input, field.id) ? input[field.id] : undefined
    const cleaned = isUnanswered(raw) ? { value: null } : cleanAnswer(field, raw)
    if (cleaned.error) return cleaned.error
    if (cleaned.value === null && field.required) return `"${field.label}" is required`
  }
  return null
}

export const hasInlineForm = (event) => {
  const fields = event?.registrationForm?.fields
  return Array.isArray(fields) && fields.length > 0
}
